import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import SimplePriceComparison from './SimplePriceComparison';
import { transducersData } from '../../data/transducersData';
import { screensData } from '../../data/screensData';
import { batteriesData } from '../../data/batteriesData';
import { shuttlesData } from '../../data/shuttlesData';
import { polesData } from '../../data/polesData';
import { bundlesData } from '../../data/bundlesData';

// Build full image URL so it works in dev (PUBLIC_URL '') and when deployed with a subpath (e.g. homepage).
const getDisplayImageSrc = (url) => {
  if (!url) return '';
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${process.env.PUBLIC_URL || ''}${path}`;
};

// Simple product page with minimal content, just product name and price comparison
const SimpleProductPage = ({ specificProductId, productName, productImage, fallbackImage, csvLookupId, csvExactMatchOnly = true, componentType, prefetchedOffers, msrp: msrpProp }) => {
  const { productId: urlProductId } = useParams();
  const productId = specificProductId || urlProductId;
  const { theme } = useTheme();
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [imageSrc, setImageSrc] = useState(() => getDisplayImageSrc(productImage));
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Update image source when productImage prop changes (use full path so subpath deployments load)
  useEffect(() => {
    setImageSrc(getDisplayImageSrc(productImage));
    setImageLoaded(false);
    setImageError(false);
  }, [productImage]);
  
  // Log image URL for debugging
  useEffect(() => {
    console.log('SimpleProductPage - Product ID:', productId);
    console.log('SimpleProductPage - Product Name:', productName);
    console.log('SimpleProductPage - Product Image URL:', productImage);
    console.log('SimpleProductPage - CSV Lookup ID:', csvLookupId);
    console.log('SimpleProductPage - Exact Match Only:', csvExactMatchOnly);
    console.log('SimpleProductPage - Current Image State:', { 
      imageSrc, 
      imageLoaded, 
      imageError,
      publicUrl: getDisplayImageSrc(productImage)
    });
    
    // Preload using the same full URL the img tag will use
    if (productImage) {
      const fullSrc = getDisplayImageSrc(productImage);
      const img = new Image();
      img.onload = () => {
        console.log('Image preload successful:', fullSrc);
        setImageLoaded(true);
        setImageError(false);
      };
      img.onerror = () => {
        console.error('Image preload failed:', fullSrc);
        setImageError(true);
        if (fullSrc !== imageSrc) setImageSrc(fullSrc);
      };
      img.src = fullSrc;
    }
  }, [productId, productName, productImage, imageSrc, imageLoaded, imageError, csvLookupId, csvExactMatchOnly]);
  
  // Get product name from props or format from productId
  const getDisplayName = () => {
    if (productName) return productName;
    if (!productId) return 'Product Details';
    
    // Format product ID for display with proper capitalization for ECHOMAP and UHD/UHD2
    const formattedName = productId
      .split('-')
      .map(word => {
        // Special case for ECHOMAP - always all caps
        if (word.toLowerCase() === 'echomap') return 'ECHOMAP';
        
        // Special case for UHD/UHD2 - always all caps
        if (word.toLowerCase() === 'uhd') return 'UHD';
        if (word.toLowerCase() === 'uhd2') return 'UHD2';
        
        // Default capitalization for other words
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
    
    return formattedName;
  };

  // Price formatting function
  const formatPrice = (price) => {
    return price.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Check if product is from Amped Outdoors
  const isAmpedOutdoors = () => {
    if (!productId) return false;
    
    // Check if productName or productId contains "Amped Outdoors"
    if (productName && productName.toLowerCase().includes('amped outdoors')) {
      return true;
    }
    
    // Check battery data for dealInfo containing Amped Outdoors
    const battery = batteriesData.find(item => {
      if (item.id === productId) return true;
      if (item.variants?.withCharger?.id === productId) return true;
      if (item.variants?.withoutCharger?.id === productId) return true;
      return false;
    });
    
    return battery && battery.dealInfo && battery.dealInfo.includes('Amped Outdoors');
  };

  // Get MSRP from product data (use msrp prop if provided, e.g. from DB-driven ProductComparisonPage)
  const getMsrp = () => {
    if (msrpProp !== undefined && msrpProp !== null) return msrpProp;
    if (!productId) return null;
    
    // Check bundles data first
    const bundle = bundlesData.find(item => item.id === productId);
    if (bundle && bundle.msrp) {
      return bundle.msrp;
    }
    
    // Check transducers data
    const transducer = transducersData.find(item => item.id === productId);
    if (transducer && transducer.msrp) {
      return transducer.msrp;
    }
    
    // Check screens data
    const screen = screensData.find(item => item.id === productId);
    if (screen && screen.msrp) {
      return screen.msrp;
    }
    
    // Check batteries data (including variants)
    const battery = batteriesData.find(item => {
      if (item.id === productId) return true;
      if (item.variants?.withCharger?.id === productId) return true;
      if (item.variants?.withoutCharger?.id === productId) return true;
      return false;
    });
    
    if (battery) {
      // Check if it's a variant first
      if (battery.variants?.withCharger?.id === productId) {
        return battery.variants.withCharger.msrp;
      }
      if (battery.variants?.withoutCharger?.id === productId) {
        return battery.variants.withoutCharger.msrp;
      }
      // Otherwise use main battery MSRP
      return battery.msrp;
    }
    
    // Check shuttles data
    const shuttle = shuttlesData.find(item => item.id === productId);
    if (shuttle && shuttle.msrp) {
      return shuttle.msrp;
    }
    
    // Check poles data
    const pole = polesData.find(item => item.id === productId);
    if (pole && pole.msrp) {
      return pole.msrp;
    }
    
    return null;
  };

  // Check if this battery has Bluetooth
  const hasBluetooth = () => {
    // Check batteries data (including variants)
    const battery = batteriesData.find(item => {
      if (item.id === productId) return true;
      if (item.variants?.withCharger?.id === productId) return true;
      if (item.variants?.withoutCharger?.id === productId) return true;
      return false;
    });

    return battery?.hasBluetooth || false;
  };
  
  // Handle image loading error
  const handleImageError = (e) => {
    console.error('Image failed to load:', imageSrc);
    setImageError(true);
    e.target.onerror = null;
    
    const fullSrc = getDisplayImageSrc(productImage);
    if (imageSrc !== fullSrc) {
      console.log('Trying fallback image path:', fullSrc);
      setImageSrc(fullSrc);
    } else if (fallbackImage) {
      // Use the provided fallback image (e.g. base64 encoded)
      console.log('Using provided fallback image');
      setImageSrc(fallbackImage);
    } else {
      // Last resort - use a dummy image
      e.target.src = `https://dummyimage.com/400x200/cccccc/000000&text=${getDisplayName()}`;
    }
  };
  
  // Handle successful image load
  const handleImageLoad = () => {
    console.log('Image loaded successfully:', imageSrc);
    setImageLoaded(true);
    setImageError(false);
  };

  // Get heading styles based on screen width
  const getHeadingStyles = () => {
    const baseStyles = {
      fontWeight: '700',
      margin: '5px auto 15px auto',
      textAlign: 'center',
      color: theme.isDarkMode ? '#fff' : '#333',
      textShadow: theme.isDarkMode ? '0 2px 4px rgba(0,0,0,0.3)' : 'none',
      lineHeight: '1.2',
      padding: '0',
      maxWidth: '90%',
      width: 'auto',
      display: 'inline-block',
      position: 'relative'
    };
    
    // Mobile styles
    if (windowWidth < 576) {
      return {
        ...baseStyles,
        fontSize: '20px',
        padding: '0 5px',
        whiteSpace: 'normal',
      };
    }
    
    // Desktop styles  
    return {
      ...baseStyles,
      fontSize: Math.min(Math.max(windowWidth * 0.025, 22), 32) + 'px', // Between 22px and 32px based on viewport
      whiteSpace: 'normal', // Allow text wrapping
      overflow: 'visible', // Show wrapped text
    };
  };

  return (
    <div style={{ 
      background: theme.colors.background,
      color: theme.colors.text,
      minHeight: '100vh',
      padding: '10px 0'
    }}>
      <div style={{ 
        maxWidth: '800px',
        margin: '0 auto',
        padding: '0 10px'
      }}>
        <header style={{
          marginBottom: '15px',
          textAlign: 'center'
        }}>
          <h1 style={getHeadingStyles()}>
            {getDisplayName()}
          </h1>
          
          {/* MSRP Display */}
          {getMsrp() && getMsrp() > 0 && (
            <div style={{
              textAlign: 'center',
              marginBottom: '20px',
              padding: '8px 0'
            }}>
              <span style={{
                fontSize: windowWidth < 576 ? '16px' : '18px',
                fontWeight: '600',
                color: theme.isDarkMode ? '#e0e0e0' : '#666',
                background: theme.isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                padding: '8px 16px',
                borderRadius: '8px',
                border: `1px solid ${theme.isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
              }}>
                MSRP: {formatPrice(getMsrp())}
              </span>
            </div>
          )}
          
          {imageSrc ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              margin: '15px 0 20px 0'
            }}>
              <div style={{
                position: 'relative',
                padding: '15px',
                background: theme.isDarkMode ? '#2a2a2a' : '#fff',
                borderRadius: '10px',
                boxShadow: theme.isDarkMode ? 
                  '0 3px 8px rgba(0,0,0,0.2)' : 
                  '0 3px 8px rgba(0,0,0,0.1)',
                border: `1px solid ${theme.isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
                maxWidth: '75%'
              }}>
                <img
                  src={imageSrc}
                  alt={getDisplayName()}
                  style={{
                    maxWidth: '100%', 
                    maxHeight: '180px',
                    objectFit: 'contain',
                    display: 'block',
                    margin: '0 auto'
                  }}
                  onError={handleImageError}
                  onLoad={handleImageLoad}
                />
                {/* Add Bluetooth icon for batteries with Bluetooth capability */}
                {hasBluetooth() && (
                  <div style={{
                    position: 'absolute',
                    top: '20px',
                    left: '20px',
                    backgroundColor: 'rgba(25, 118, 210, 0.8)',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10
                  }}>
                    <svg 
                      width="18" 
                      height="18" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="white" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <polyline points="6.5 6.5 17.5 17.5 12 23 12 1 17.5 6.5 6.5 17.5"></polyline>
                    </svg>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{
              margin: '10px 0 20px 0',
              textAlign: 'center',
              color: theme.colors.textSecondary
            }}>
              <p>No product image available</p>
              {/* Debug information */}
              {process.env.NODE_ENV === 'development' && (
                <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '5px' }}>
                  <p>Debug: Expected image path was {productImage}</p>
                </div>
              )}
            </div>
          )}
        </header>
        
        <SimplePriceComparison 
          specificProductId={productId} 
          csvLookupId={csvLookupId} 
          csvExactMatchOnly={csvExactMatchOnly}
          msrp={getMsrp()}
          isAmpedOutdoors={isAmpedOutdoors()}
          prefetchedOffers={prefetchedOffers}
        />
      </div>
    </div>
  );
};

export default SimpleProductPage; 